/**
 * „Angebot versendet" – der Startschuss für die Nachfass-Uhr.
 *
 * Der Versand selbst passiert in Outlook. Hier wird nur festgehalten, dass er
 * stattgefunden hat. Zwei Angaben zählen:
 *
 *   Datum     – ab hier läuft die Uhr. Rückdatieren ist ausdrücklich erlaubt:
 *               ein Angebot, das freitags rausging und montags eingetragen
 *               wird, zählt trotzdem ab Freitag.
 *   Versender – in dessen Namen und mit dessen Signatur geht die automatische
 *               Erinnerung raus. Voreingestellt ist, wer klickt; wer für eine
 *               Kollegin nachträgt, stellt um.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import { betragFormatieren, betragLesen, dat } from '../hilfen/format';
import type { Angebot, AngebotAntwort } from '../typen';

export function VersendetModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen, nutzer } = useKora();
  const meta = useMeta();
  const heute = meta.heute;

  const [datum, setDatum] = useState(heute);
  /*
   * Angebotssumme – Pflicht, und bewusst leer vorbelegt.
   *
   * Ein Vertriebler kennt die Summe im Moment des Versendens immer. Stünde hier
   * ein Vorschlag, würde er bestätigt statt geprüft; eine 0 würde die Kopfsumme
   * in „Meine Aufgaben" still verfälschen. Steht die Summe schon am Vorgang, ist
   * sie bereits am Vorgang und muss nur bestätigt werden.
   */
  const [summe, setSumme] = useState(betragFormatieren(String(angebot.summe ?? '')));
  const [fehler, setFehler] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.sende<AngebotAntwort>(`/angebote/${angebot.id}/versendet`, {
        datum,
        summe: betragLesen(summe),
      }),
    onSuccess: (antwort) => {
      onFertig();
      if (antwort.toast) toastZeigen(antwort.toast);
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  function bestaetigen() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datum)) {
      setFehler('Bitte ein Versanddatum wählen.');
      return;
    }
    if (datum > heute) {
      setFehler('Das Versanddatum kann nicht in der Zukunft liegen.');
      return;
    }
    const betrag = betragLesen(summe);
    if (!betrag || betrag <= 0) {
      setFehler(
        'Bitte die Angebotssumme angeben. Ohne sie lässt sich nicht sagen, wie viel Geld beim Kunden liegt.',
      );
      return;
    }
    mutation.mutate();
  }

  const rueckdatiert = datum < heute;

  return (
    <Modal
      titel="Angebot versendet"
      untertitel={`${angebot.nr}${angebot.variante}_${angebot.prefix}`}
      breite={470}
      abstandOben={70}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={bestaetigen} disabled={mutation.isPending}>
            {mutation.isPending ? 'Wird gespeichert …' : 'Bestätigen'}
          </KnopfPrimaer>
        </>
      }
    >
      <label className="modal-feld">
        <span>Versendet am</span>
        <input
          autoFocus
          type="date"
          className="modal-eingabe"
          value={datum}
          max={heute}
          onChange={(e) => {
            setDatum(e.target.value);
            setFehler('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') bestaetigen();
          }}
        />
      </label>

      <label className="modal-feld">
        <span>Angebotssumme netto</span>
        <input
          className={`modal-eingabe ${fehler && !betragLesen(summe) ? 'modal-eingabe--fehler' : ''}`}
          value={summe}
          inputMode="decimal"
          placeholder="z. B. 42.800"
          onChange={(e) => {
            setSumme(betragFormatieren(e.target.value));
            setFehler('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') bestaetigen();
          }}
        />
        <span className="modal-hinweis">
          Der Auftragswert ist davon getrennt – er entsteht erst bei „Auftrag erteilt" und darf
          abweichen.
        </span>
      </label>

      {/*
        Keine Auswahl: Versender ist, wer einträgt.
        Die Erinnerung geht später mit seiner Signatur hinaus – jemanden
        anderen einzutragen hieße, in dessen Namen zu schreiben, ohne dass er
        davon weiß. Wer für einen Kollegen einträgt, meldet sich als er an.
      */}
      <div className="modal-hinweis">
        Eingetragen als <strong>{nutzer?.anzeigename ?? nutzer?.name}</strong>. Die automatische
        Erinnerung geht später in deinem Namen und mit deiner Signatur raus.
      </div>

      <div className="modal-hinweis">
        {rueckdatiert
          ? `Die Nachfass-Uhr rechnet ab ${dat(datum)}, nicht ab heute.`
          : 'Ab heute läuft die Nachfass-Uhr.'}
      </div>

      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
